import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  agentsApi,
  ApiError,
  type PendingAgentAction,
  type AgentActionIntent,
} from "../lib/api";
import { MarkdownBody } from "../components/MarkdownBody";
import { pageLabel } from "../lib/pageLabel";

export function AgentsPendingPage() {
  const q = useQuery({
    queryKey: ["agents-pending"],
    queryFn: agentsApi.pending,
    refetchInterval: 30_000,
  });

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Pending approvals</h1>
          <p className="text-ink-600 text-sm mt-1">
            Actions queued by agents in <code className="bg-ink-100 px-1 rounded text-xs">confirm</code> trust phase. Approve to execute the intent; reject to discard.{" "}
            <Link to="/agents" className="text-accent hover:underline">All agents</Link>
          </p>
        </div>
      </div>

      {q.isLoading && (
        <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>
      )}
      {q.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          Failed: {String((q.error as Error).message)}
        </div>
      )}
      {q.data && (
        q.data.pending.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3">
            {q.data.pending.map((p) => <PendingCard key={p.id} action={p} />)}
          </div>
        )
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-8 text-center">
      <div className="text-3xl mb-2">✓</div>
      <p className="text-ink-700 font-medium">No pending approvals</p>
      <p className="text-sm text-ink-500 mt-1">
        Agents in <code className="bg-ink-100 px-1 rounded text-xs">confirm</code> trust phase will queue actions here for your review. Promote an agent via{" "}
        <code className="bg-ink-100 px-1 rounded text-xs">rankforge agents promote &lt;id&gt; confirm</code>.
      </p>
    </div>
  );
}

function PendingCard({ action }: { action: PendingAgentAction }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showIntent, setShowIntent] = useState(false);

  const approve = useMutation({
    mutationFn: () => agentsApi.approve(action.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents-pending"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
    onSettled: () => setPending(null),
  });
  const reject = useMutation({
    mutationFn: () => agentsApi.reject(action.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents-pending"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
    onSettled: () => setPending(null),
  });

  const proposedAgo = formatAgo(action.proposedAt);
  const intent = action.intent;
  const intentSummary = intent ? summarizeIntent(intent) : "(no intent — legacy)";

  return (
    <div className="bg-white rounded-xl border border-ink-100 shadow-sm overflow-hidden">
      <div className="px-5 py-4 flex items-start gap-4">
        <div className="text-2xl pt-0.5">{action.agentEmoji ?? "🤖"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Link
              to={`/agents/${action.agentId}`}
              className="font-semibold text-ink-900 hover:text-accent hover:underline"
            >
              {action.agentDisplayName}
            </Link>
            <span className="text-xs uppercase tracking-wide font-medium text-purple-700">
              {action.actionKind}
            </span>
            {action.siteDomain && (
              <Link to={`/sites/${action.siteId}`} className="text-xs text-accent hover:underline">
                {action.siteDomain}
              </Link>
            )}
            <span className="text-xs text-ink-400 ml-auto">{proposedAgo}</span>
          </div>

          {/* Title from intent payload, when it's a raise-alert. */}
          {intent && intent.kind === "raise-alert" && (
            <div className="mt-2">
              <SeverityBadge severity={intent.payload.severity} />
              <span className="ml-2 text-ink-900 font-medium">{intent.payload.title}</span>
              {/* Target page (when the executable carries one — Owen's
                  brief intents do; alert-only intents like Drew's drift
                  alert don't). Surfaced inline so the operator sees
                  what page the action will modify BEFORE clicking
                  Approve, without expanding the description body. */}
              {(() => {
                const tgt = (intent.payload.executable as { targetUrl?: string } | undefined)?.targetUrl;
                if (!tgt) return null;
                return (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs text-ink-600">
                    <span className="text-ink-400">·</span>
                    <span className="text-ink-500">page:</span>
                    <a
                      href={tgt}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-ink-800 hover:text-accent hover:underline"
                      title={tgt}
                    >
                      {pageLabel(tgt)}
                    </a>
                  </span>
                );
              })()}
            </div>
          )}
          {intent && intent.kind === "auto-resolve-alert" && (
            <div className="mt-2 text-sm text-ink-700">
              Auto-resolve alert <code className="bg-ink-100 px-1 rounded text-xs">{intent.dedupKey}</code>
              {intent.note && <span className="text-ink-500"> — {intent.note}</span>}
            </div>
          )}
          {intent && intent.kind === "regenerate-with-brief" && (
            <div className="mt-2 text-sm">
              <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-semibold uppercase tracking-wide">REGEN</span>
              <span className="ml-2 text-ink-900 font-medium">{intent.payload.domain}</span>
              <span className="ml-1 text-ink-600">— rewrite for <code className="bg-ink-100 px-1 rounded text-xs">"{intent.payload.keyword}"</code></span>
              <div className="text-xs text-ink-500 mt-1">
                On approve: queues a follow-up alert with the <code className="bg-ink-50 px-1 rounded">pnpm rankforge optimize</code> command for the operator to run.
              </div>
            </div>
          )}
          {intent && intent.kind === "add-internal-link" && (
            <div className="mt-2 text-sm">
              <span className="inline-block px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-semibold uppercase tracking-wide">LINK</span>
              <span className="ml-2 text-ink-900 font-medium">{intent.payload.domain}</span>
              <div className="text-xs text-ink-700 mt-1">
                Add link with anchor <code className="bg-ink-100 px-1 rounded">"{intent.payload.anchorText}"</code> → {intent.payload.targetUrl}
              </div>
              <div className="text-xs text-ink-500 mt-1">
                On approve: appends the link to the source blog post's <code className="bg-ink-50 px-1 rounded">body_html</code> in DB. Run <code className="bg-ink-50 px-1 rounded">pnpm rankforge blog-render --domain {intent.payload.domain} --deploy</code> after batch-approving links to ship.
              </div>
            </div>
          )}

          {/* Description preview from intent — collapsed by default */}
          {intent && intent.kind === "raise-alert" && intent.payload.description && (
            <details className="mt-2 text-xs text-ink-600">
              <summary className="cursor-pointer hover:text-ink-900">
                Show description
              </summary>
              <div className="mt-2 bg-ink-50 p-3 rounded border border-ink-100 max-h-96 overflow-auto">
                <MarkdownBody source={intent.payload.description} />
              </div>
            </details>
          )}

          {/* Raw intent + input state for power users */}
          <details className="mt-2 text-[11px] text-ink-500" open={showIntent} onToggle={(e) => setShowIntent((e.target as HTMLDetailsElement).open)}>
            <summary className="cursor-pointer hover:text-ink-700">Intent JSON ({intentSummary})</summary>
            <pre className="mt-2 font-mono bg-ink-50 p-2 rounded border border-ink-100 overflow-auto max-h-64">{JSON.stringify({ intent: action.intent, inputState: action.inputState }, null, 2)}</pre>
          </details>

          {error && <div className="text-xs text-red-700 mt-2">{error}</div>}
        </div>
      </div>

      <div className="bg-ink-50 px-5 py-3 border-t border-ink-100 flex items-center justify-end gap-2">
        <button
          onClick={() => {
            setError(null);
            setPending("reject");
            reject.mutate();
          }}
          disabled={pending !== null}
          className="text-sm px-3 py-1.5 rounded bg-white border border-ink-200 text-ink-700 hover:bg-ink-100 disabled:opacity-50"
        >
          {pending === "reject" ? "Rejecting…" : "Reject"}
        </button>
        <button
          onClick={() => {
            setError(null);
            setPending("approve");
            approve.mutate();
          }}
          disabled={pending !== null || !action.intent}
          className="text-sm px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          title={!action.intent ? "No intent attached — can't approve" : undefined}
        >
          {pending === "approve" ? "Approving…" : "Approve"}
        </button>
      </div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: "info" | "warn" | "critical" }) {
  const color =
    severity === "critical"
      ? "bg-red-100 text-red-800"
      : severity === "warn"
        ? "bg-amber-100 text-amber-800"
        : "bg-blue-100 text-blue-800";
  return (
    <span className={`uppercase tracking-wide text-[10px] font-semibold px-1.5 py-0.5 rounded ${color}`}>
      {severity}
    </span>
  );
}

function summarizeIntent(intent: AgentActionIntent): string {
  if (intent.kind === "raise-alert") return `raise ${intent.payload.category}`;
  if (intent.kind === "auto-resolve-alert") return "auto-resolve";
  return "unknown";
}

function formatAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}
